import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { KundmarkeResponse } from './KundmarkeResponse';
import { debounce } from 'underscore';
import { extractQueryParams, tryDecodeUriComponent } from '../miscFunctions';

export type KundmarkeState = {
  kundmarkeKravs: boolean;
  visaKundmarke: boolean;
  kundmarke?: string;
  setKundmarke: (value: string) => void;
};

const KundmarkeStateContext = React.createContext<KundmarkeState | undefined>(
  undefined
);

async function getKundmarke(): Promise<KundmarkeResponse> {
  if (process.env.NODE_ENV === 'development') {
    return new Promise<KundmarkeResponse>((resolve) => {
      setTimeout(() => {
        resolve({
          kundmarke: '',
          kundmarkeIsRequired: false,
          kundmarkeIsAnvandarnamn: false,
        });
      }, 500);
    });
  }

  const resp = await fetch('/fsok/seam/resource/kundmarke');
  return (await resp.json()) as KundmarkeResponse;
}

async function postKundmarke(kundmarke: string): Promise<void | Response> {
  if (process.env.NODE_ENV === 'development') {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, 500);
    });
  }

  return fetch('/fsok/seam/resource/kundmarke', {
    method: 'POST',
    body: kundmarke,
  });
}

const KundmarkeContextProvider: React.FC = ({ children }) => {
  const queryParams = extractQueryParams(window.location.search);
  const [kundmarke, setKundmarke] = useState<string>(
    queryParams.kundmarke || ''
  );
  const [kundmarkeKravs, setKundmarkeKravs] = useState(
    window.fsokConfig.kundmarkeIsRequired === 'true'
  );
  const [visaKundmarke] = useState(window.fsokConfig.showKundmarke === 'true');

  const handleKundmarkeChange = (value: string): void => {
    debouncedSetKundmarke(value);
    setKundmarke(value);
  };

  const value = {
    kundmarke,
    kundmarkeKravs,
    visaKundmarke,
    setKundmarke: handleKundmarkeChange,
  };

  const debouncedSetKundmarke = useCallback((kundmarke: string) => {
    debounce((kundmarke: string) => postKundmarke(kundmarke), 1000)(kundmarke);
  }, []);

  useEffect(() => {
    if (kundmarke) {
      return handleKundmarkeChange(kundmarke);
    }
    let cancelled = false;
    const doFetchKundmarke = async () => {
      const kundmarkeResponse = await getKundmarke();
      if (!cancelled) {
        setKundmarke(tryDecodeUriComponent(kundmarkeResponse.kundmarke));
        setKundmarkeKravs(kundmarkeResponse.kundmarkeIsRequired);
      }
    };
    doFetchKundmarke();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <KundmarkeStateContext.Provider value={value}>
      {children}
    </KundmarkeStateContext.Provider>
  );
};

function useKundmarkeContext() {
  const context = React.useContext(KundmarkeStateContext);
  if (context === undefined) {
    throw new Error('React must be used within a KundmarkeContextProvider');
  }
  return context;
}

const kundmarkeMaxLength = 255;

export { KundmarkeContextProvider, useKundmarkeContext, kundmarkeMaxLength };
